// components/LockMappingModal.js
//
// Admin-only modal for mapping a TTLock device to the current property.
// Used by LockStatusPanel's empty-state when admin clicks "Map a Lock".
//
// Flow:
//   1. On open: fetch unmapped locks via lockApi.getUnmappedLocks()
//   2. Admin selects one from the list
//   3. Submit POSTs to lockApi.mapLock({ lockId, propertyId })
//   4. On success: parent re-fetches the panel, modal closes
//
// Follows the existing modal pattern in SecurePropertyDetails.js — same
// className structure (modal show d-block, modal-dialog, modal-content)
// for visual consistency.

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import lockApi from '../services/lockApi';
import useLanguage from '../hooks/useLanguage';
import { batteryColor } from '../utils/lockUiHelpers';

// Normalize a string for matching: lowercase + remove ":" so MAC searches
// work whether the user types "F406A7" or "F4:06:A7".
const matchNormalize = (s) => String(s ?? '').toLowerCase().replace(/:/g, '');

const LockMappingModal = ({ propertyId, onClose, onMapped }) => {
  const { t, language } = useLanguage();
  const [unmapped, setUnmapped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedLockId, setSelectedLockId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [search, setSearch] = useState('');

  // Client-side filter on alias / lockId / MAC. Memoized so we don't
  // re-filter on every parent re-render unless inputs change.
  const filtered = useMemo(() => {
    const q = matchNormalize(search.trim());
    if (!q) return unmapped;
    return unmapped.filter((l) =>
      matchNormalize(l.lockAlias).includes(q)
      || matchNormalize(l.lockName).includes(q)
      || matchNormalize(l.lockId).includes(q)
      || matchNormalize(l.lockMac).includes(q)
    );
  }, [unmapped, search]);

  const fetchUnmapped = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await lockApi.getUnmappedLocks();
      setUnmapped(response.locks || []);
    } catch (err) {
      setLoadError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUnmapped(); }, [fetchUnmapped]);

  const handleSubmit = async () => {
    if (!selectedLockId) {
      setSubmitError(t('locks.mappingModal.error.selectRequired'));
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await lockApi.mapLock({ lockId: selectedLockId, propertyId });
      onMapped && onMapped();
      onClose();
    } catch (err) {
      setSubmitError(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content" dir={language === 'ar' ? 'rtl' : 'ltr'}>
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-key me-2"></i>
              {t('locks.mappingModal.title')}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={submitting}
              aria-label="Close"
            ></button>
          </div>

          <div className="modal-body">
            <p className="mb-2">{t('locks.mappingModal.instruction')}</p>

            {/* Search — visible only once we have locks loaded successfully */}
            {!loading && !loadError && unmapped.length > 0 && (
              <div className="input-group mb-3">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('locks.mappingModal.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
            )}

            {loading && (
              <div className="text-center p-3">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">{t('locks.mappingModal.loading')}</span>
                </div>
                <p className="mt-2 mb-0 text-muted">{t('locks.mappingModal.loading')}</p>
              </div>
            )}

            {loadError && (
              <div className="alert alert-danger d-flex justify-content-between align-items-center mb-0">
                <span><strong>{t('locks.mappingModal.error.loadFailed')}:</strong> {loadError}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={fetchUnmapped}
                >
                  {t('locks.panel.error.retry')}
                </button>
              </div>
            )}

            {!loading && !loadError && unmapped.length === 0 && (
              <div className="alert alert-info mb-0">
                <i className="bi bi-info-circle me-2"></i>
                {t('locks.mappingModal.empty')}
              </div>
            )}

            {!loading && !loadError && unmapped.length > 0 && filtered.length === 0 && (
              <div className="alert alert-light text-center mb-0" role="alert">
                <i className="bi bi-search me-2"></i>
                {t('locks.mappingModal.noSearchMatches')}
              </div>
            )}

            {!loading && !loadError && filtered.length > 0 && (
              <div className="list-group">
                {filtered.map((lock) => {
                  const isSelected = selectedLockId === lock.lockId;
                  return (
                    <label
                      key={lock.lockId}
                      className={`list-group-item list-group-item-action d-flex align-items-center gap-3 ${isSelected ? 'active' : ''}`}
                      style={{ cursor: 'pointer' }}
                    >
                      <input
                        type="radio"
                        name="lockSelection"
                        className="form-check-input flex-shrink-0 m-0"
                        checked={isSelected}
                        onChange={() => setSelectedLockId(lock.lockId)}
                      />
                      <div className="flex-grow-1">
                        <div style={{ fontWeight: 500 }}>{lock.lockAlias || lock.lockName || '—'}</div>
                        <small className={isSelected ? '' : 'text-muted'}>
                          {t('locks.mappingModal.lockId')}: {lock.lockId}
                          {lock.lockMac && ` · ${lock.lockMac}`}
                        </small>
                      </div>
                      <div className="text-end" style={{ minWidth: '120px' }}>
                        <div style={{ color: isSelected ? 'inherit' : batteryColor(lock.electricQuantity) }}>
                          <i className="bi bi-battery-half me-1"></i>
                          {lock.electricQuantity != null ? `${lock.electricQuantity}%` : '—'}
                        </div>
                        <small className={isSelected ? '' : 'text-muted'}>
                          <i className={`bi bi-${lock.isOnline ? 'wifi' : 'wifi-off'} me-1`}></i>
                          {lock.isOnline
                            ? t('locks.mappingModal.online')
                            : t('locks.mappingModal.offline')}
                        </small>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {submitError && (
              <div className="alert alert-danger mt-3 mb-0">
                <strong>{t('locks.mappingModal.error.title')}:</strong> {submitError}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              {t('locks.mappingModal.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={submitting || !selectedLockId || loading || loadError}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {t('locks.mappingModal.submitting')}
                </>
              ) : (
                t('locks.mappingModal.submit')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LockMappingModal;
